import {
    Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges,
    SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import * as moment from 'moment';
import Litepicker from 'litepicker';

import { TimeTrackingService } from '../../services/time-tracking.service';
import { DataService } from '@app/shared/common/auth/data.service';
import { TimeEntry, AddTimeEntryInput, UpdateTimeEntryInput } from '../../models/time-tracking.models';
import { AppSessionService } from '@shared/common/session/app-session.service';
import { TaskServiceProxy, ProjectMembers, CommonLookupServiceProxy, LookUpTables } from '@shared/service-proxies/service-proxies';
import { AppConsts } from '@shared/commonHelper/AppConsts';

declare const $: any;

const LP_PREV_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"></path></svg>';
const LP_NEXT_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"></path></svg>';

interface EntryGroup {
    label: string;
    dateKey: string;
    total: string;
    entries: TimeEntry[];
}

interface FilterState {
    personIds: number[];
    dateFrom: string;
    dateTo: string;
}

@Component({
    selector: 'vf-task-time-overlay',
    templateUrl: './task-time-overlay.component.html',
    styleUrls: ['./task-time-overlay.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskTimeOverlayComponent implements OnInit, OnDestroy, OnChanges {

    @Input() taskId: number;
    @Input() taskName: string;
    @Input() projectId: number;
    @Input() isOpen = false;
    @Output() close = new EventEmitter<void>();

    entries: TimeEntry[] = [];
    groups: EntryGroup[] = [];
    isLoading = false;
    showAddForm = false;
    editingEntryId: number | null = null;
    showFilterPopover = false;
    confirmDeleteId: number | null = null;
    isSaving = false;
    isDeleting = false;

    // Add / edit form state
    formDate = '';
    formPersonId: number;
    formPersonName = '';
    formPersonAvatarUrl = '';
    formStartTime = '';
    formEndTime = '';
    formDurationDisplay = '';

    // Person picker state
    projectMembers: ProjectMembers[] = [];
    personSearchText = '';
    isLoadingMembers = false;

    // Time pickers
    readonly timeOptions: string[] = Array.from({ length: 48 }, (_, i) => {
        const h = Math.floor(i / 2);
        const m = (i % 2) * 30;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    });
    showStartTimeDropdown = false;
    showEndTimeDropdown = false;

    // Filter state
    filter: FilterState = { personIds: [], dateFrom: '', dateTo: '' };
    filterPersonIds: number[] = [];
    filterDateFrom = '';
    filterDateTo = '';

    maxDate = '';
    minDate = '';

    // Width tracks the edit-task-modal right pane (pane - 3px) so this overlay matches it as the user drags the separator.
    modalWidth = 572;

    private datePicker: Litepicker;
    private filterFromPicker: Litepicker;
    private filterToPicker: Litepicker;
    private subs = new Subscription();

    private personSearchDebounce: any;

    constructor(
        public ttService: TimeTrackingService,
        private dataService: DataService,
        private sessionService: AppSessionService,
        private taskProxy: TaskServiceProxy,
        private commonLookupProxy: CommonLookupServiceProxy,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
       this.maxDate = moment().format('YYYY-MM-DD');
       this.minDate = moment().subtract(30, 'days').format('YYYY-MM-DD');
        this.formPersonId = this.sessionService.userId as number;
        this.modalWidth = this.dataService.editTaskRightPaneWidth - 3;
        this.subs.add(this.dataService.editTaskRightPaneWidthChange.subscribe(w => {
            this.modalWidth = w - 3;
            this.cdr.markForCheck();
        }));
        this.subs.add(this.dataService.timerStopped.subscribe(() => this.loadEntries()));
        if (this.taskId && this.isOpen) this.loadEntries();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.isOpen && this.isOpen && this.taskId) {
            this.loadEntries();
        }
    }

    loadEntries(): void {
        if (!this.taskId) return;
        this.isLoading = true;
        // Eagerly load project members so the permission gate (canManageEntry)
        // knows whether the current user is a project manager before any
        // 3-dot button renders. Without this, the menu would appear allowed
        // for anyone until the user opened the Add/Edit form.
        this.loadProjectMembers();
        this.cdr.markForCheck();
        this.ttService.getTaskTimeEntries(this.taskId).subscribe(entries => {
            this.entries = entries || [];
            this.buildGroups();
            this.isLoading = false;
            this.cdr.markForCheck();
        });
    }

    private buildGroups(): void {
        let filtered = [...this.entries];
        if (this.filter.personIds.length > 0) {
            filtered = filtered.filter(e => this.filter.personIds.includes(e.userId));
        }
        if (this.filter.dateFrom) {
            filtered = filtered.filter(e => e.date >= this.filter.dateFrom);
        }
        if (this.filter.dateTo) {
            filtered = filtered.filter(e => e.date <= this.filter.dateTo);
        }
        filtered.sort((a, b) => {
            const aDate = a.date || '';
            const bDate = b.date || '';
            const aStart = a.startTime || '';
            const bStart = b.startTime || '';
            return bDate.localeCompare(aDate) || bStart.localeCompare(aStart);
        });

        const map = new Map<string, TimeEntry[]>();
        filtered.forEach(e => {
            const key = e.date;
            if (!key) { return; }
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(e);
        });

        const today = moment().format('YYYY-MM-DD');
        const yesterday = moment().subtract(1, 'day').format('YYYY-MM-DD');
        const currentYear = moment().year();

        this.groups = Array.from(map.entries()).map(([dateKey, items]) => {
            let label: string;
            if (dateKey === today) label = 'Today';
            else if (dateKey === yesterday) label = 'Yesterday';
            else {
                const m = moment(dateKey);
                label = m.year() === currentYear ? m.format('MMM D') : m.format('MMM D, YYYY');
            }
            // Sum precise seconds (not rounded minutes) so the date header reflects
            // real elapsed time, matching the per-row "Xs" / "Xm" display.
            const totalSecs = items.reduce((acc, e) => acc + (e.durationSeconds || 0), 0);
            return { label, dateKey, total: this.ttService.formatDuration(totalSecs), entries: items };
        });
    }

    get totalTime(): string {
        return this.ttService.formatTotalMinutes(this.entries);
    }

    formatTime(t: string): string {
        if (!t) return '';
        const [h, m] = t.split(':').map(Number);
        const period = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 || 12;
        return `${h12}:${String(m).padStart(2, '0')} ${period}`;
    }

    onTimeChange(): void {
        if (this.formStartTime && this.formEndTime) {
            const [sh, sm] = this.formStartTime.split(':').map(Number);
            const [eh, em] = this.formEndTime.split(':').map(Number);
            // formatDuration now takes seconds; HH:mm picker is minute-precision.
            const diffSecs = ((eh * 60 + em) - (sh * 60 + sm)) * 60;
            this.formDurationDisplay = diffSecs > 0 ? this.ttService.formatDuration(diffSecs) : '';
        } else {
            this.formDurationDisplay = '';
        }
        this.cdr.markForCheck();
    }

    get isFormValid(): boolean {
        if (!this.formDate || !this.formStartTime || !this.formEndTime) return false;
        if (this.formDate > this.maxDate || this.formDate < this.minDate) return false;
        const [sh, sm] = this.formStartTime.split(':').map(Number);
        const [eh, em] = this.formEndTime.split(':').map(Number);
        const diff = (eh * 60 + em) - (sh * 60 + sm);
        if (diff < 1) return false;      // End must be after start, min 1 minute
        if (diff > 1440) return false;    // Max 24 hours
        return true;
    }

    get formValidationError(): string {
        if (!this.formDate || !this.formStartTime || !this.formEndTime) return '';
        if (this.formDate > this.maxDate) return 'Time cannot be added for a future date.';
        if (this.formDate < this.minDate) return 'Time can only be added up to 30 days back.';
        if (!this.formStartTime || !this.formEndTime) return '';
        const [sh, sm] = this.formStartTime.split(':').map(Number);
        const [eh, em] = this.formEndTime.split(':').map(Number);
        const diff = (eh * 60 + em) - (sh * 60 + sm);
        if (diff <= 0) return 'End time must be after start time.';
        if (diff < 1) return 'Entry must be at least 1 minute.';
        if (diff > 1440) return 'Entry cannot be longer than 24 hours.';
        return '';
    }

    get hasOverlap(): boolean {
        if (!this.formDate || !this.formStartTime || !this.formEndTime) return false;
        return this.entries.some(e => {
            if (e.id === this.editingEntryId) return false;
            if (e.date !== this.formDate) return false;
            return this.formStartTime < e.endTime && this.formEndTime > e.startTime;
        });
    }

    get formDateDisplay(): string {
        if (!this.formDate) return '';
        return moment(this.formDate).format('ddd, MMM D, YYYY');
    }

    getDatePickerElementId(): string {
        return 'vf-time-overlay-date-picker-' + (this.taskId || 'na');
    }

    resetForm(): void {
        const sessionUser = this.sessionService.user;
        const firstName = sessionUser && sessionUser.name ? sessionUser.name : '';
        const lastName = sessionUser && sessionUser.surname ? sessionUser.surname : '';

        this.formDate = moment().format('YYYY-MM-DD');
        this.formStartTime = '';
        this.formEndTime = '';
        this.formDurationDisplay = '';
        this.formPersonId = this.sessionService.userId as number;
        this.formPersonName = (firstName + ' ' + lastName).trim();
        this.formPersonAvatarUrl = sessionUser && sessionUser.profilePictureUrl ? sessionUser.profilePictureUrl : '';
        this.personSearchText = '';
        this.showStartTimeDropdown = false;
        this.showEndTimeDropdown = false;
    }

    openAddForm(): void {
        this.editingEntryId = null;
        this.resetForm();
        this.showAddForm = true;
        this.cdr.markForCheck();
        this.loadProjectMembers();
        this.initDatePicker();
    }

    cancelAdd(): void {
        this.showAddForm = false;
        this.editingEntryId = null;
        this.destroyDatePicker();
        this.cdr.markForCheck();
    }

    saveEntry(): void {
        if (!this.isFormValid || this.isSaving) return;
        this.isSaving = true;

        if (this.editingEntryId !== null) {
            const input: UpdateTimeEntryInput = {
                id: this.editingEntryId,
                taskId: this.taskId,
                userId: this.sessionService.userId as number,
                date: this.formDate,
                startTime: this.formStartTime,
                endTime: this.formEndTime
            };
            this.ttService.updateTimeEntry(input).subscribe({
                next: updated => {
                    const idx = this.entries.findIndex(e => e.id === this.editingEntryId);
                    if (idx >= 0) this.entries[idx] = updated;
                    else this.entries.push(updated);
                    this.buildGroups();
                    this.editingEntryId = null;
                    this.showAddForm = false;
                    this.isSaving = false;
                    this.destroyDatePicker();
                    this.cdr.markForCheck();
                    this.dataService.refreshTaskTimeEntries.next(this.taskId);
                },
                error: () => { this.isSaving = false; this.cdr.markForCheck(); }
            });
        } else {
            const input: AddTimeEntryInput = {
                taskId: this.taskId,
                userId: this.sessionService.userId,
                date: this.formDate,
                startTime: this.formStartTime,
                endTime: this.formEndTime
            };
            this.ttService.addTimeEntry(input).subscribe({
                next: entry => {
                    this.entries.unshift(entry);
                    this.buildGroups();
                    this.showAddForm = false;
                    this.isSaving = false;
                    this.destroyDatePicker();
                    this.cdr.markForCheck();
                    this.dataService.refreshTaskTimeEntries.next(this.taskId);
                },
                error: () => { this.isSaving = false; this.cdr.markForCheck(); }
            });
        }
    }

    // ── Permissions ─────────────────────────────────────────────────────────
    /**
     * Project managers (memberType === 1) can edit/delete any entry on this
     * task. Looked up against the loaded `projectMembers` for `this.projectId`.
     */
    get isProjectManager(): boolean {
        const uid = this.sessionService.userId;
        if (!uid) { return false; }
        const me = (this.projectMembers || []).find(m => m.userId === uid);
        return !!me && (me as any).memberType === 1;
    }

    canManageEntry(entry: TimeEntry | null | undefined): boolean {
        if (!entry) { return false; }
        if (entry.userId === this.sessionService.userId) { return true; }
        return this.isProjectManager;
    }

    editEntry(entry: TimeEntry): void {
        if (!this.canManageEntry(entry)) { return; }
        this.showAddForm = false;
        this.destroyDatePicker();
        this.editingEntryId = entry.id;
        this.formDate = entry.date;
        this.formPersonId = entry.userId;
        this.formPersonName = entry.userName || '';
        this.formPersonAvatarUrl = entry.userAvatarUrl || '';
        this.formStartTime = entry.startTime;
        this.formEndTime = entry.endTime;
        this.personSearchText = '';
        this.showStartTimeDropdown = false;
        this.showEndTimeDropdown = false;
        this.onTimeChange();
        this.cdr.markForCheck();
        this.loadProjectMembers();
    }

    cancelEdit(): void {
        this.editingEntryId = null;
        this.cdr.markForCheck();
    }

    requestDelete(id: number): void {
        const target = this.entries.find(e => e.id === id);
        if (!this.canManageEntry(target)) { return; }
        this.confirmDeleteId = id;
        this.cdr.markForCheck();
    }

    cancelDelete(): void {
        this.confirmDeleteId = null;
        this.cdr.markForCheck();
    }

    confirmDelete(): void {
        if (!this.confirmDeleteId || this.isDeleting) return;
        this.isDeleting = true;
        const id = this.confirmDeleteId;
        this.ttService.deleteTimeEntry(id).subscribe({
            next: () => {
                this.entries = this.entries.filter(e => e.id !== id);
                this.buildGroups();
                this.confirmDeleteId = null;
                this.isDeleting = false;
                this.cdr.markForCheck();
                this.dataService.refreshTaskTimeEntries.next(this.taskId);
            },
            error: () => { this.isDeleting = false; this.cdr.markForCheck(); }
        });
    }

    toggleFilterPopover(): void {
        this.showFilterPopover = !this.showFilterPopover;
        if (this.showFilterPopover) {
            // Sync working state from applied filter, then init pickers after DOM renders
            this.filterPersonIds = [...this.filter.personIds];
            this.filterDateFrom = this.filter.dateFrom || '';
            this.filterDateTo = this.filter.dateTo || '';
            this.cdr.markForCheck();
            this.initFilterDatePickers();
        } else {
            this.destroyFilterDatePickers();
        }
    }

    closeFilterPopover(): void {
        this.showFilterPopover = false;
        this.destroyFilterDatePickers();
        this.cdr.markForCheck();
    }

    applyFilter(): void {
        this.filter = {
            personIds: [...this.filterPersonIds],
            dateFrom: this.filterDateFrom,
            dateTo: this.filterDateTo
        };
        this.buildGroups();
        this.showFilterPopover = false;
        this.destroyFilterDatePickers();
        this.cdr.markForCheck();
    }

    clearFilter(): void {
        this.filterPersonIds = [];
        this.filterDateFrom = '';
        this.filterDateTo = '';
        this.filter = { personIds: [], dateFrom: '', dateTo: '' };
        this.buildGroups();
        this.showFilterPopover = false;
        this.destroyFilterDatePickers();
        this.cdr.markForCheck();
    }

    // ── Filter person helpers ───────────────────────────────────────────────
    get filterPeopleOptions(): { id: number; name: string }[] {
        const seen = new Map<number, string>();
        (this.entries || []).forEach(e => {
            if (e.userId && !seen.has(e.userId)) {
                seen.set(e.userId, this.firstNameOf(e.userName));
            }
        });
        return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
    }

    private firstNameOf(full: string): string {
        if (!full) { return '?'; }
        return full.trim().split(/\s+/)[0];
    }

    isPersonSelected(userId: number): boolean {
        return this.filterPersonIds.indexOf(userId) > -1;
    }

    togglePersonFilter(userId: number): void {
        const idx = this.filterPersonIds.indexOf(userId);
        if (idx > -1) {
            this.filterPersonIds.splice(idx, 1);
        } else {
            this.filterPersonIds.push(userId);
        }
        this.cdr.markForCheck();
    }

    selectAllPersons(): void {
        this.filterPersonIds = [];
        this.cdr.markForCheck();
    }

    get isAllPersonsSelected(): boolean {
        return this.filterPersonIds.length === 0;
    }

    get hasActiveFilterDraft(): boolean {
        return this.filterPersonIds.length > 0 || !!this.filterDateFrom || !!this.filterDateTo;
    }

    /** Number of applied filter categories — drives the trigger button's active state + count badge. */
    get activeFilterCount(): number {
        let n = 0;
        if (this.filter.personIds && this.filter.personIds.length > 0) { n++; }
        if (this.filter.dateFrom || this.filter.dateTo) { n++; }
        return n;
    }

    formatFilterDateDisplay(value: string): string {
        if (!value) { return ''; }
        return moment(value).format('MMM D, YYYY');
    }

    // ── Filter Litepickers ──────────────────────────────────────────────────
    private initFilterDatePickers(): void {
        this.destroyFilterDatePickers();
        setTimeout(() => {
            const fromEl = document.getElementById(this.getFilterFromPickerId()) as HTMLInputElement | null;
            const toEl = document.getElementById(this.getFilterToPickerId()) as HTMLInputElement | null;
            if (fromEl) {
                try {
                    this.filterFromPicker = new Litepicker({
                        element: fromEl,
                        format: 'MMM D, YYYY',
                        autoApply: true,
                        firstDay: 7,
                        singleMode: true,
                        startDate: this.filterDateFrom ? moment(this.filterDateFrom).toDate() : '',
                        // Cap From at the current To value so user can't pick a From > To.
                        maxDate: this.filterDateTo ? moment(this.filterDateTo).toDate() : null,
                        buttonText: { previousMonth: LP_PREV_SVG, nextMonth: LP_NEXT_SVG, apply: 'Apply', cancel: 'Cancel', reset: '' },
                        setup: (picker) => {
                            picker.on('show', () => {
                                const ui = (picker as any).ui as HTMLElement | undefined;
                                if (!ui) { return; }
                                ui.classList.add('vf-compact-litepicker');
                                const pill = fromEl.closest('.vf-time-pill') as HTMLElement | null;
                                if (pill) {
                                    const r = pill.getBoundingClientRect();
                                    const sx = window.scrollX || (window as any).pageXOffset || 0;
                                    const sy = window.scrollY || (window as any).pageYOffset || 0;
                                    ui.style.top = (r.bottom + sy + 4) + 'px';
                                    ui.style.left = (r.left + sx) + 'px';
                                }
                            });
                            picker.on('selected', (date1) => {
                                if (date1 && date1.dateInstance) {
                                    this.filterDateFrom = moment(date1.dateInstance).format('YYYY-MM-DD');
                                    // Push the new From up as the To picker's min so To can't be earlier.
                                    if (this.filterToPicker) {
                                        try { (this.filterToPicker as any).setOptions({ minDate: date1.dateInstance }); } catch { /* ignore */ }
                                    }
                                    this.cdr.markForCheck();
                                }
                                picker.hide();
                            });
                        }
                    });
                } catch (e) { console.error('Filter from picker init failed', e); }
            }
            if (toEl) {
                try {
                    this.filterToPicker = new Litepicker({
                        element: toEl,
                        format: 'MMM D, YYYY',
                        autoApply: true,
                        firstDay: 7,
                        singleMode: true,
                        startDate: this.filterDateTo ? moment(this.filterDateTo).toDate() : '',
                        // Floor To at the current From value so user can't pick a To < From.
                        minDate: this.filterDateFrom ? moment(this.filterDateFrom).toDate() : null,
                        buttonText: { previousMonth: LP_PREV_SVG, nextMonth: LP_NEXT_SVG, apply: 'Apply', cancel: 'Cancel', reset: '' },
                        setup: (picker) => {
                            picker.on('show', () => {
                                const ui = (picker as any).ui as HTMLElement | undefined;
                                if (!ui) { return; }
                                ui.classList.add('vf-compact-litepicker');
                                const pill = toEl.closest('.vf-time-pill') as HTMLElement | null;
                                if (pill) {
                                    const r = pill.getBoundingClientRect();
                                    const sx = window.scrollX || (window as any).pageXOffset || 0;
                                    const sy = window.scrollY || (window as any).pageYOffset || 0;
                                    ui.style.top = (r.bottom + sy + 4) + 'px';
                                    ui.style.left = (r.left + sx) + 'px';
                                }
                            });
                            picker.on('selected', (date1) => {
                                if (date1 && date1.dateInstance) {
                                    this.filterDateTo = moment(date1.dateInstance).format('YYYY-MM-DD');
                                    // Push the new To down as the From picker's max so From can't be later.
                                    if (this.filterFromPicker) {
                                        try { (this.filterFromPicker as any).setOptions({ maxDate: date1.dateInstance }); } catch { /* ignore */ }
                                    }
                                    this.cdr.markForCheck();
                                }
                                picker.hide();
                            });
                        }
                    });
                } catch (e) { console.error('Filter to picker init failed', e); }
            }
        }, 100);
    }

    openFilterFromPicker(): void {
        if (this.filterFromPicker) { this.filterFromPicker.show(); }
    }

    openFilterToPicker(): void {
        if (this.filterToPicker) { this.filterToPicker.show(); }
    }

    private destroyFilterDatePickers(): void {
        if (this.filterFromPicker) {
            try { this.filterFromPicker.destroy(); } catch { /* ignore */ }
            this.filterFromPicker = undefined;
        }
        if (this.filterToPicker) {
            try { this.filterToPicker.destroy(); } catch { /* ignore */ }
            this.filterToPicker = undefined;
        }
    }

    getFilterFromPickerId(): string {
        return 'vf-time-overlay-filter-from-' + (this.taskId || 'na');
    }

    getFilterToPickerId(): string {
        return 'vf-time-overlay-filter-to-' + (this.taskId || 'na');
    }

    getInitials(name: string): string {
        if (!name) return '?';
        return name.split(' ').map(w => w[0]).join('').toUpperCase().substring(0, 2);
    }

    onClose(): void {
        this.showAddForm = false;
        this.editingEntryId = null;
        this.destroyDatePicker();
        this.close.emit();
    }

    // ── Litepicker (Add form date) ──────────────────────────────────────────
    private initDatePicker(): void {
        this.destroyDatePicker();
        setTimeout(() => {
            const el = document.getElementById(this.getDatePickerElementId()) as HTMLInputElement | null;
            if (!el) { return; }
            try {
                this.datePicker = new Litepicker({
                    element: el,
                    format: 'MMM D, YYYY',
                    autoApply: true,
                    firstDay: 7,
                    singleMode: true,
                    startDate: this.formDate ? moment(this.formDate).toDate() : '',
                    // Manual entries log time that already happened — block future dates.
                    maxDate: moment().endOf('day').toDate(),
                    buttonText: { previousMonth: LP_PREV_SVG, nextMonth: LP_NEXT_SVG, apply: 'Apply', cancel: 'Cancel', reset: '' },
                    setup: (picker) => {
                        picker.on('show', () => {
                            const ui = (picker as any).ui as HTMLElement | undefined;
                            if (!ui) { return; }
                            ui.classList.add('vf-compact-litepicker');
                            const pill = el.closest('.vf-time-pill') as HTMLElement | null;
                            if (pill) {
                                const r = pill.getBoundingClientRect();
                                const sx = window.scrollX || (window as any).pageXOffset || 0;
                                const sy = window.scrollY || (window as any).pageYOffset || 0;
                                ui.style.top = (r.bottom + sy + 4) + 'px';
                                ui.style.left = (r.left + sx) + 'px';
                            }
                        });
                        picker.on('selected', (date1) => {
                            if (date1 && date1.dateInstance) {
                                this.formDate = moment(date1.dateInstance).format('YYYY-MM-DD');
                                this.cdr.markForCheck();
                            }
                            picker.hide();
                        });
                    }
                });
            } catch (e) {
                console.error('Litepicker init failed', e);
            }
        }, 100);
    }

    openDatePicker(): void {
        if (this.datePicker) {
            this.datePicker.show();
        }
    }

    private destroyDatePicker(): void {
        if (this.datePicker) {
            try { this.datePicker.destroy(); } catch { /* ignore */ }
            this.datePicker = undefined;
        }
    }

    // ── Person picker ───────────────────────────────────────────────────────
    private loadProjectMembers(): void {
        const seen = new Set<number>();
        const local: ProjectMembers[] = [];
        if (this.formPersonId) {
            seen.add(this.formPersonId);
            local.push({ userId: this.formPersonId, memberName: this.formPersonName, profilePictureUrl: this.formPersonAvatarUrl } as any);
        }
        this.entries.forEach(e => {
            if (e.userId && !seen.has(e.userId)) {
                seen.add(e.userId);
                local.push({ userId: e.userId, memberName: e.userName, profilePictureUrl: e.userAvatarUrl } as any);
            }
        });
        this.projectMembers = local;
        this.cdr.markForCheck();

        if (!this.projectId) { return; }
        this.isLoadingMembers = true;
        this.taskProxy.getProjectMembers(this.projectId, null, null, null).subscribe({
            next: res => {
                const api = (res.responseData || []).filter(m => !m.isDeleted && m.userId);
                if (api.length > 0) {
                    const apiIds = new Set(api.map(m => m.userId));
                    const extras = this.projectMembers.filter(m => !apiIds.has(m.userId));
                    this.projectMembers = [...api, ...extras];
                }
                this.isLoadingMembers = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.isLoadingMembers = false;
                this.cdr.markForCheck();
            }
        });
    }

    get visibleMembers(): ProjectMembers[] {
        const selectedId = this.formPersonId;
        const all = (this.projectMembers || []).filter(m => m.userId !== selectedId);
        if (!this.personSearchText || !this.personSearchText.trim()) { return all; }
        const q = this.personSearchText.toLowerCase();
        return all.filter(m => m.memberName && m.memberName.toLowerCase().includes(q));
    }

    onOpenPersonDropdown(): void {
        this.personSearchText = '';
        this.loadProjectMembers();
        setTimeout(() => {
            const el = document.querySelector('.vf-time-person-drop input[name="personSearchText"]') as HTMLInputElement;
            if (el) { el.focus(); }
        }, 100);
    }

    onPersonSearchInput(): void {
        clearTimeout(this.personSearchDebounce);
        this.personSearchDebounce = setTimeout(() => {
            const q = (this.personSearchText || '').trim();
            if (q === '') {
                this.loadProjectMembers();
            } else {
                this.searchMembers(q);
            }
        }, 300);
    }

    private searchMembers(query: string): void {
        this.isLoadingMembers = true;
        this.cdr.markForCheck();
        this.commonLookupProxy.getAllForCascadeLookupTable(
            this.projectId || undefined,
            query,
            false,
            undefined,
            LookUpTables._1,
            false,
            [],
            '',
            0,
            AppConsts.maxTypeAheadResults
        ).subscribe({
            next: result => {
                const items = (result || []).map(u => ({
                    userId: u.id,
                    memberName: u.name,
                    profilePictureUrl: u.profilePictureUrl
                } as any as ProjectMembers));
                this.projectMembers = items;
                this.isLoadingMembers = false;
                this.cdr.markForCheck();
            },
            error: () => {
                this.isLoadingMembers = false;
                this.cdr.markForCheck();
            }
        });
    }

    selectPerson(member: ProjectMembers): void {
        if (!member) { return; }
        this.formPersonId = member.userId as number;
        this.formPersonName = member.memberName || '';
        this.formPersonAvatarUrl = member.profilePictureUrl || '';
        this.personSearchText = '';
        this.closePersonDropdown();
        this.cdr.markForCheck();
    }

    clearPerson(): void {
        this.formPersonId = null;
        this.formPersonName = '';
        this.formPersonAvatarUrl = '';
        this.personSearchText = '';
        this.cdr.markForCheck();
    }

    private closePersonDropdown(): void {
        try {
            $('.vf-time-person-drop').find('.dropdown-menu').removeClass('show');
            $('.vf-time-person-drop [data-toggle="dropdown"]').attr('aria-expanded', 'false');
            $('.vf-time-person-drop').removeClass('show');
        } catch { /* ignore */ }
    }

    // ── Time picker dropdowns ───────────────────────────────────────────────
    onStartTimeFocus(): void {
        this.showStartTimeDropdown = true;
        this.showEndTimeDropdown = false;
        this.cdr.markForCheck();
    }

    onEndTimeFocus(): void {
        this.showEndTimeDropdown = true;
        this.showStartTimeDropdown = false;
        this.cdr.markForCheck();
    }

    hideStartTimeDropdown(): void {
        setTimeout(() => {
            this.showStartTimeDropdown = false;
            this.cdr.markForCheck();
        }, 150);
    }

    hideEndTimeDropdown(): void {
        setTimeout(() => {
            this.showEndTimeDropdown = false;
            this.cdr.markForCheck();
        }, 150);
    }

    selectStartTime(t: string): void {
        this.formStartTime = t;
        this.showStartTimeDropdown = false;
        this.onTimeChange();
        this.cdr.markForCheck();
    }

    selectEndTime(t: string): void {
        this.formEndTime = t;
        this.showEndTimeDropdown = false;
        this.onTimeChange();
        this.cdr.markForCheck();
    }

    getTimePeriodLabel(index: number): string | null {
        if (index === 0) return 'Night';
        if (index === 12) return 'Morning';
        if (index === 24) return 'Afternoon';
        if (index === 36) return 'Evening';
        return null;
    }

    ngOnDestroy(): void {
        this.destroyDatePicker();
        this.destroyFilterDatePickers();
        clearTimeout(this.personSearchDebounce);
        this.subs.unsubscribe();
    }
}
